import { DocumentData, DocumentSnapshot } from "@firebase/firestore-types";
import { plainToClass } from "class-transformer";
import firebase from "firebase/compat/app";

/**
 * Converts ISO date strings in an object to Firestore-like { seconds, nanoseconds }
 * so that core's convertDocToClass / Model.fromDoc can consume API JSON responses.
 * Use before passing API response as doc.data() to fromDoc.
 */
export function normalizeTimestampsInData(
  obj: Record<string, unknown>
): Record<string, unknown> {
  const out = { ...obj };
  for (const [key, value] of Object.entries(out)) {
    if (
      typeof value === "string" &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)
    ) {
      const ms = Date.parse(value);
      if (Number.isFinite(ms)) {
        out[key] = {
          seconds: Math.floor(ms / 1000),
          nanoseconds: Math.round((ms % 1000) * 1e6),
        };
      }
    } else if (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      !(value as { seconds?: number }).seconds
    ) {
      out[key] = normalizeTimestampsInData(value as Record<string, unknown>);
    } else if (Array.isArray(value)) {
      out[key] = value.map((item) =>
        item !== null &&
        typeof item === "object" &&
        !Array.isArray(item)
          ? normalizeTimestampsInData(item as Record<string, unknown>)
          : item
      );
    }
  }
  return out;
}

const isTimestamp = (
  timestamp: any
): timestamp is firebase.firestore.Timestamp => {
  return (
    !!timestamp &&
    typeof timestamp === "object" &&
    timestamp.hasOwnProperty("seconds") &&
    timestamp.hasOwnProperty("nanoseconds")
  );
};

export function convertDocToClass(
  doc: DocumentSnapshot<DocumentData>,
  cls: { new (...args: any[]): any }
) {
  const JObject = {
    id: doc.id,
    ...Object.fromEntries(
      Object.entries(doc.data()).map(([key, value]) => {
        value = isTimestamp(value) ? value.toDate() : value;
        return [key, value];
      })
    ),
  };
  return plainToClass(cls, JObject);
}
